import React, { useState, useMemo } from "react";
import "./DataGrid.css";

/**
 * Advanced Data Grid Component with Pagination and Search
 * Provides a data table with configurable columns, search, pagination, and actions
 *
 * @param {Object} props
 * @param {Array} props.data - Array of data objects to display
 * @param {Array} props.columns - Array of column definitions {key, label, sortable, width}
 * @param {number} props.itemsPerPage - Items per page (default: 10)
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onRowClick - Callback when row is clicked
 * @param {Function} props.onEdit - Callback for edit action
 * @param {Function} props.onDelete - Callback for delete action
 * @param {boolean} props.showActions - Show action buttons (default: true)
 */
const DataGrid = ({
  data = [],
  columns = [],
  itemsPerPage = 10,
  className = "",
  onRowClick,
  onEdit,
  onDelete,
  showActions = true,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    return data.filter((item) =>
      Object.values(item).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [data, searchTerm]);

  // Sort filtered data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [filteredData, sortConfig]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage);

  // Handle sort
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Reset pagination when search changes
  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  return (
    <div className={`data-grid-wrapper ${className}`}>
      {/* Search Bar */}
      <div className="data-grid-search">
        <input
          type="text"
          placeholder="Search in all columns..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          className="search-input"
        />
        {searchTerm && (
          <button onClick={() => handleSearch("")} className="search-clear-btn">
            ✕
          </button>
        )}
      </div>

      {/* Grid/Table */}
      {paginatedData.length > 0 ? (
        <>
          <div className="data-grid">
            {/* Header */}
            <div className="grid-header">
              {columns.map((column) => (
                <div
                  key={column.key}
                  className={`grid-cell header-cell ${
                    column.sortable ? "sortable" : ""
                  }`}
                  style={{ width: column.width || "auto" }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  {column.label}
                  {column.sortable && sortConfig.key === column.key && (
                    <span className="sort-indicator">
                      {sortConfig.direction === "asc" ? " ↑" : " ↓"}
                    </span>
                  )}
                </div>
              ))}
              {showActions && (
                <div
                  className="grid-cell header-cell actions-header"
                  style={{ width: "140px" }}
                >
                  Actions
                </div>
              )}
            </div>

            {/* Rows */}
            {paginatedData.map((row, rowIndex) => (
              <div
                key={rowIndex}
                className="grid-row"
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map((column) => (
                  <div
                    key={column.key}
                    className="grid-cell"
                    style={{ width: column.width || "auto" }}
                    data-label={column.label}
                  >
                    {column.render
                      ? column.render(row[column.key], row)
                      : row[column.key]}
                  </div>
                ))}
                {showActions && (
                  <div
                    className="grid-cell actions-cell"
                    style={{ width: "140px" }}
                  >
                    {onEdit && (
                      <button
                        className="action-btn edit-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(row);
                        }}
                        title="Edit"
                      >
                        ✎ Edit
                      </button>
                    )}
                    {onDelete && (
                      <button
                        className="action-btn delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(row);
                        }}
                        title="Delete"
                      >
                        🗑 Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="data-grid-pagination">
            <div className="pagination-info">
              Showing {startIndex + 1} to{" "}
              {Math.min(startIndex + itemsPerPage, sortedData.length)} of{" "}
              {sortedData.length} results
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                ← Previous
              </button>

              <div className="pagination-pages">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`pagination-page ${
                        currentPage === page ? "active" : ""
                      }`}
                    >
                      {page}
                    </button>
                  ),
                )}
              </div>

              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next →
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="data-grid-empty">
          <p>No data available</p>
        </div>
      )}
    </div>
  );
};

export default DataGrid;
